import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { tourStepsHome } from "./tourStepsHome";
import { tourStepsAbout } from "./tourStepsAbout";
import GuidedTour from "../../components/GuidedTour";
import { useTour } from "../../context/TourContext";
import Layout from "../../components/Layout";
import BubbleText from "../../components/BubbleText";
import FeedbackForm from "../../components/FeedbackForm";
import logo from "../../assets/full-logo.png";
import { useTheme } from "../../context/ThemeContext";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * @fileoverview Main landing page component showcasing the application's features and upcoming functionality.
 * 
 * @component Home
 * @description Landing page that introduces Race The Clock, its features, and target audience.
 * Includes animated sections, feature highlights, and upcoming features preview.
 * 
 * Features:
 * - Responsive hero section with logo
 * - Animated scroll-based decorative elements
 * - Feature highlights grid
 * - Coming soon section
 * - Theme-aware styling
 * - Guided tour integration
 * - Feedback form integration
 * 
 * Sections:
 * - Hero section with platform introduction
 * - Target audience list
 * - Features grid (Custom Collections, Adjustable Speed, etc.)
 * - Coming soon features preview
 * - Animated decorative elements using motion.svg
 * 
 * Theme Integration:
 * - Background color/image adaptation
 * - Text color contrast handling
 * - Consistent styling across sections
 */

const Home: React.FC = () => {
  const [isTourRunning, setIsTourRunning] = useState<boolean>(false);
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const { toursCompleted } = useTour();
  const location = useLocation();
  const isAboutPage = location.pathname === "/about";
  const { theme } = useTheme();
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const tourType = isAboutPage ? "about" : "home";
    if (!toursCompleted[tourType]) {
      setIsTourRunning(true);
    }
  }, [toursCompleted, isAboutPage]);

  const handleTourComplete = () => {
    setIsTourRunning(false);
  };

  const handleStartTour = () => {
    setIsTourRunning(true);
    setCurrentTourStep(0);
  };

  const content = (
    <div
      className="text-theme-text min-h-screen"
      style={{
        color: theme.textColor,
      }}
    >
      <div className="relative">
        {/* Hero Section */}
        <section className="hero-section px-4 py-12">
          <div className="relative mb-12 text-center">
            <img
              src={logo}
              alt="Race The Clock Logo"
              className="mx-auto h-auto max-h-[min(16rem,40vh)] max-w-[min(28rem,80%)] object-contain"
            />
          </div>
          <div className="mx-auto max-w-4xl text-center">
            <p
              className={`mb-8 text-lg `}
            >
              Race The Clock is an innovative, web-based learning tool designed
              to help students of all ages improve their recognition and recall
              skills across various subjects. Race the Clock can benefit:
            </p>

            <ul
              className={`audience-list mx-auto mb-8 max-w-md space-y-2 text-left `}
            >
              <li>•Elementary School Teachers</li>
              <li>•High School Teachers</li>
              <li>•Higher Education professors and students</li>
              <li>•Students of all ages practicing recognition skills</li>
              <li>•Adult learners in specialized fields</li>
            </ul>

            <p
              className={`text-lg `}
            >
              Our platform originated as a solution for early childhood teachers
              to help students achieve the goal of reading 60 letters per minute
              and has evolved into a versatile learning tool for students of all
              ages.
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section relative">
          <div className="relative h-24">
            <motion.svg
              viewBox="0 0 1440 320"
              className="absolute bottom-0 left-0 size-full"
              preserveAspectRatio="none"
              style={{
                fill: "none",
                strokeWidth: 8,
                strokeLinecap: "round",
                stroke: useTransform(
                  scrollYProgress,
                  [0, 1],
                  ["url(#gradient)", "url(#gradientEnd)"],
                ),
              }}
            >
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop
                    offset="0%"
                    style={{ stopColor: "#000000", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#00008B", stopOpacity: 1 }}
                  />
                </linearGradient>
                <linearGradient
                  id="gradientEnd"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    style={{ stopColor: "#00008B", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#000000", stopOpacity: 1 }}
                  />
                </linearGradient>
              </defs>
              <path d="M0,160 C480,400 960,-80 1440,160" />
            </motion.svg>
          </div>
          <div className="relative mb-12 text-center">
            <h2
              className={`relative z-10 text-4xl `}
            >
              <BubbleText>Features</BubbleText>
            </h2>
          </div>
          <div className="features-grid mx-auto grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2">
            <div className="text-center">
              <h3 className="mb-2 text-xl font-bold text-pink-500">
                Custom Collections
              </h3>
              <p className="text-theme-text">
                Create and customize your own collections with letters, numbers,
                pictures, words, and more.
              </p>
            </div>
            <div className="text-center">
              <h3 className="mb-2 text-xl font-bold text-blue-400">
                Adjustable Speed
              </h3>
              <p className="text-theme-text">
                Control the display speed to match your students' current skill
                levels
              </p>
            </div>
            <div className="text-center">
              <h3 className="mb-2 text-xl font-bold text-green-500">
                Full Screen Practice
              </h3>
              <p className="text-theme-text">
                Distraction-free practice mode for focused learning session.
              </p>
            </div>
            <div className="text-center">
              <h3 className="mb-2 text-xl font-bold text-purple-500">
                Theme Customization
              </h3>
              <p className="text-theme-text">
                Personalize your experience with different themes and color
                options.
              </p>
            </div>
          </div>
        </section>

        {/* Coming Soon Section */}
        <section className="coming-soon-section px-4 py-12">
          <div className="relative h-24">
            <motion.svg
              viewBox="0 0 1440 320"
              className="absolute bottom-0 left-0 size-full"
              preserveAspectRatio="none"
              style={{
                fill: "none",
                strokeWidth: 8,
                strokeLinecap: "round",
                stroke: useTransform(
                  scrollYProgress,
                  [0, 1],
                  ["url(#gradient)", "url(#gradientEnd)"],
                ),
              }}
            >
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop
                    offset="0%"
                    style={{ stopColor: "#000000", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#00008B", stopOpacity: 1 }}
                  />
                </linearGradient>
                <linearGradient
                  id="gradientEnd"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop
                    offset="0%"
                    style={{ stopColor: "#00008B", stopOpacity: 1 }}
                  />
                  <stop
                    offset="100%"
                    style={{ stopColor: "#000000", stopOpacity: 1 }}
                  />
                </linearGradient>
              </defs>
              <path d="M0,160 C480,400 960,-80 1440,160" />
            </motion.svg>
          </div>
          <div className="relative mb-8 flex items-center justify-center">
            <h2
              className={`relative z-10 text-4xl `}
            >
              <BubbleText>Coming Soon</BubbleText>
            </h2>
          </div>
          <div className="mx-auto max-w-4xl">
            <ul className="space-y-4 text-center">
              <li className="text-orange-500">Name generator</li>
              <p className="text-theme-text">
                Generate a random name for your collection
              </p>
              <li className="text-orange-500">Games</li>
              <p className="text-theme-text">
                Earn badges and achievements as you progress in your learning
                journey
              </p>
              <li className="text-orange-500">Advanced Analytics</li>
              <p className="text-theme-text">
                Detailed insights and progress tracking for teachers and
                learners
              </p>
              <li className="text-orange-500">Interactive Games</li>
              <p className="text-theme-text">
                Engaging matching games and interactive learning activities
              </p>
              <li className="text-orange-500">Student Reports</li>
              <p className="text-theme-text">
                Track and manage individual student progress and achievements
              </p>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );

  return (
    <>
      <Layout
        onStartTour={handleStartTour}
        setTourName={() => {}}
        setCurrentTourStep={setCurrentTourStep}
        setShowFeedback={setShowFeedback}
      >
        {content}
      </Layout>

      <GuidedTour
        steps={isAboutPage ? tourStepsAbout() : tourStepsHome()}
        isRunning={isTourRunning}
        onComplete={handleTourComplete}
        currentStep={currentTourStep}
        onStepChange={(step) => setCurrentTourStep(step)}
        tourName={isAboutPage ? "about" : "home"}
      />

      {showFeedback && <FeedbackForm onClose={() => setShowFeedback(false)} />}
    </>
  );
};

export default Home;
